import graphql__58ae03908f8625ce2bf5 from "./__generated__/CreateAttributeDefinitionDialogMutation.graphql.ts";import * as React from "react";
import {useIntl} from "react-intl";
import {commitMutation, graphql} from "react-relay";
import Section from "../form/Section";
import Field from "../form/Field";
import Row from "../form/Row";
import Label from "../form/Label";
import * as yup from "yup";
import FormDialog from "../dialog/FormDialog";
import type {CreateAttributeDefinitionDialogMutation} from "./__generated__/CreateAttributeDefinitionDialogMutation.graphql";
import {useRelayEnvironment} from "react-relay/hooks";
import Checkbox from "../form/Checkbox";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../../contexts/ErrorContext";

type Props = {
    handleCloseDialog: () => {}
}

const CreateAttributeDefinitionDialog: React.FC<Props> = ({handleCloseDialog}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();

    const handleSubmission = (input) => {
        // Transform entityTypes from checkbox values to array
        const entityTypes: string[] = [];
        if (input.entityTypeContact) entityTypes.push('contact');
        if (input.entityTypeAccount) entityTypes.push('account');

        const transformedInput = {
            name: input.name,
            label: input.label,
            type: input.type,
            entityTypes: entityTypes
        };

        commitMutationWithErrorHandling<CreateAttributeDefinitionDialogMutation>(
            relayEnvironment,
            {
                mutation: graphql__58ae03908f8625ce2bf5,
                variables: {
                    input: transformedInput
                },
                updater: (store) => {
                    // Get the newly created attribute definition edge from the mutation response
                    const payload = store.getRootField('createAttributeDefinition');
                    const newEdge = payload?.getLinkedRecord('attributeDefinitionEdge');

                    if (newEdge) {
                        // Get the root query field that holds the attribute definitions connection
                        const root = store.getRoot();
                        const attributeDefinitionsConnection = root.getLinkedRecord('attributeDefinitions');

                        if (attributeDefinitionsConnection) {
                            const existingEdges = attributeDefinitionsConnection.getLinkedRecords('edges') || [];
                            // Add the new edge to the beginning of the edges array
                            attributeDefinitionsConnection.setLinkedRecords([newEdge, ...existingEdges], 'edges');
                        }
                    }
                },
                onCompleted: (response, errors) => {
                    if (errors && errors.length > 0) {
                        console.error(errors);
                    }
                    handleCloseDialog();
                },
                onError: (error) => {
                    console.error(error);
                    handleCloseDialog();
                }
            },
            addError,
            {
                customErrorMessage: intl.formatMessage({id: 'errors.failedToCreateAttributeDefinition'})
            }
        )
    }

    const validationSchema = yup.object().shape({
        name: yup.string()
            .matches(/^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/, intl.formatMessage({id: 'validation.field.attributeNamePattern'}))
            .max(100, intl.formatMessage({id: 'validation.field.nameTooLong'}))
            .required(intl.formatMessage({id: 'validation.field.required'})),
        label: yup.string()
            .min(2, intl.formatMessage({id: 'validation.field.labelTooShort'}))
            .max(255, intl.formatMessage({id: 'validation.field.labelTooLong'}))
            .required(intl.formatMessage({id: 'validation.field.required'})),
        type: yup.string()
            .oneOf(['STRING', 'INTEGER', 'DATE', 'BOOLEAN'], intl.formatMessage({id: 'validation.field.invalidType'}))
            .required(intl.formatMessage({id: 'validation.field.required'})),
        entityTypeContact: yup.bool(),
        entityTypeAccount: yup.bool()
    });

    const initialValues = {
        type: 'STRING',
        entityTypeContact: true,
        entityTypeAccount: false
    }

    return (
        <FormDialog
            handleCloseDialog={handleCloseDialog}
            handleSubmission={handleSubmission}
            initialValues={initialValues}
            validationSchema={validationSchema}
            headline={intl.formatMessage({id: 'dialog.CreateAttributeDefinitionDialog.headline'})}
            submitLabel={intl.formatMessage({id: 'button.create'})}
        >
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'attributeDefinition.name'})}</Label>
                    <Field name="name" type="text" placeholder="platform.last_login" autoFocus={true}/>
                    <p className="mt-1 text-sm text-gray-500">{intl.formatMessage({id: 'attributeDefinition.name.hint'})}</p>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'attributeDefinition.label'})}</Label>
                    <Field name="label" type="text" placeholder={intl.formatMessage({id: 'attributeDefinition.label.placeholder'})}/>
                    <p className="mt-1 text-sm text-gray-500">{intl.formatMessage({id: 'attributeDefinition.label.hint'})}</p>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'attributeDefinition.type'})}</Label>
                    <Field name="type" as="select">
                        <option value="STRING">{intl.formatMessage({id: 'attributeDefinition.type.string'})}</option>
                        <option value="INTEGER">{intl.formatMessage({id: 'attributeDefinition.type.integer'})}</option>
                        <option value="DATE">{intl.formatMessage({id: 'attributeDefinition.type.date'})}</option>
                        <option value="BOOLEAN">{intl.formatMessage({id: 'attributeDefinition.type.boolean'})}</option>
                    </Field>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'attributeDefinition.entityTypes'})}</Label>
                    <div className="space-y-2">
                        <div className="flex items-center">
                            <Checkbox name="entityTypeContact"/>
                            <span className="ml-2 text-sm text-gray-700">{intl.formatMessage({id: 'attributeDefinition.entityTypes.contact'})}</span>
                        </div>
                        <div className="flex items-center">
                            <Checkbox name="entityTypeAccount"/>
                            <span className="ml-2 text-sm text-gray-700">{intl.formatMessage({id: 'attributeDefinition.entityTypes.account'})}</span>
                        </div>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{intl.formatMessage({id: 'attributeDefinition.entityTypes.hint'})}</p>
                </Row>
            </Section>
        </FormDialog>
    )
}

export default CreateAttributeDefinitionDialog;
